import {
  createEmptyEncoding,
  renderToCanvas,
  encodingToHex,
  hexToEncoding,
} from "bitframes";
import { getBackground } from "./constants.js";
// import { WorkerTasks } from "../deprecated/worker-tasks.js";

const MAX_MEMORY = 2151000000; // bytes

const BIG_NUMBER = 9007199254740;
let globalID = 0;

const EMPTY_ENCODING_LIST_HEX = [
  encodingToHex(createEmptyEncoding({ system: 0x00 })),
  encodingToHex(createEmptyEncoding({ system: 0x01 })),
];

const noop = () => {};

function findOldestCanvasIndex(list) {
  let minTime = Infinity;
  let minIndex = -1;
  for (let i = 0; i < list.length; i++) {
    const a = list[i];
    if (a.lastSeen < minTime) {
      minIndex = i;
      minTime = a.lastSeen;
    }
  }
  return minIndex;
}

// const worker = new Worker(
//   new URL("./fast-canvas-pool-worker.js", import.meta.url),
//   {
//     type: "module",
//   }
// );

export function CanvasPoolManager(opts = {}) {
  const {
    ignoreIntersection = () => false,
    visibilityChange = () => {},
    afterIntersection = () => {},
  } = opts;

  const canvasPool = CanvasPool(opts);
  const observer = new IntersectionObserver((entries) => {
    for (let entry of entries) {
      const visible = entry.isIntersecting;
      const target = entry.target;
      if (ignoreIntersection(target)) {
        continue;
      }

      const wasVisible = target.dataset.visible === "true";
      target.dataset.visible = visible ? "true" : "false";
      if (visible !== wasVisible) {
        visibilityChange(target, visible);
        if (visible) {
          canvasPool.addToRenderQueue(target);
        } else {
          canvasPool.removeFromRenderQueue(target);
        }
      }
    }
    afterIntersection(entries);
  });

  return {
    addToRenderQueue: (n) => canvasPool.addToRenderQueue(n),
    removeFromRenderQueue: (n) => canvasPool.removeFromRenderQueue(n),
    destroy() {
      canvasPool.destroy();
    },
    action(node, opts = {}) {
      observer.observe(node);
      // if (opts.index <= 16) {
      //   addToRenderQueue(node);
      // }
      return {
        destroy() {
          canvasPool.removeFromRenderQueue(node);
          observer.unobserve(node);
        },
      };
    },
  };
}

export function SyncCanvasRenderer(opts = {}) {
  return {
    getContext(canvas, attribs) {
      return canvas.getContext("2d", attribs);
    },
    render(encodingHex, node, renderID, opts = {}) {
      const { context } = opts;
      context.save();
      context.globalAlpha = 1;
      context.clearRect(0, 0, opts.width, opts.height);
      renderToCanvas({
        context,
        ...opts,
        encoding: hexToEncoding(encodingHex),
      });
      context.restore();
      return {
        paint: true,
      };
    },
  };
}

export function AsyncCanvasRenderer2D(opts = {}) {
  return {
    getContext(canvas, attribs) {
      return canvas.getContext("2d", attribs);
    },
    render(encodingHex, node, renderID, opts = {}) {
      const { context } = opts;
      let raf = null;
      return {
        paint: true,
        abort: () => {
          if (raf != null) {
            cancelAnimationFrame(raf);
            raf = null;
          }
        },
        promise: new Promise((resolve) => {
          raf = requestAnimationFrame(() => {
            if (node.renderID === renderID) {
              context.save();
              context.globalAlpha = 1;
              context.clearRect(0, 0, opts.width, opts.height);
              renderToCanvas({
                context,
                ...opts,
                encoding: hexToEncoding(encodingHex),
              });
              context.restore();
              resolve();
            }
          });
        }),
      };
    },
  };
}

// export function AsyncRenderer(opts = {}) {
//   // const queue = Array(1)
//   //   .fill()
//   //   .map(() => {
//   //     const worker = new Worker(
//   //       new URL("./fast-canvas-pool-worker.js", import.meta.url),
//   //       {
//   //         type: "module",
//   //       }
//   //     );
//   //     return WorkerTasks(worker);
//   //   });
//   // let pointer = 0;
//   const worker = new Worker(
//     new URL("./fast-canvas-pool-worker.js", import.meta.url),
//     {
//       type: "module",
//     }
//   );
//   const queue = WorkerTasks(worker);
//   let canvasID = 0;

//   return {
//     getContext(canvas, attribs) {
//       // return canvas.getContext("bitmaprenderer", attribs);
//       return canvas.transferControlToOffscreen();
//     },
//     render(encodingHex, node, renderID, opts = {}) {
//       const {
//         context,
//         width,
//         height,
//         hatch = true,
//         hatchContours = true,
//         colorSpace = "srgb",
//       } = opts;

//       if (!node._hasInitWorker) {
//         node._hasInitWorker = true;
//         node._workerCanvasID = canvasID++;
//         if (canvasID >= Number.MAX_SAFE_INTEGER) {
//           canvasID = 0;
//         }
//         worker.postMessage(
//           {
//             type: "create",
//             canvas: context,
//             canvasKey: node._workerCanvasID,
//           },
//           [context]
//         );
//       }

//       // const q = queue[pointer++ % queue.length];
//       const q = queue;
//       const task = q.createTask({
//         canvasKey: node._workerCanvasID,
//         encodingHex,
//         width,
//         height,
//         hatch,
//         hatchContours,
//         colorSpace,
//       });
//       const promise = task.promise.then((data) => {
//         if (data && data.bitmap && node.renderID === renderID) {
//           // console.log(data.bitmap);
//           // context.transferFromImageBitmap(data.bitmap);
//         }
//       });

//       return {
//         paint: true,
//         abort: () => task.dispose(),
//         promise,
//       };
//     },
//   };
// }

export function CachedRenderer(opts = {}) {
  const { size = 16 } = opts;

  if (size <= 0) throw new Error("cache size must be > 0");

  const canvases = Array(size)
    .fill()
    .map(() => {
      const canvas = document.createElement("canvas");
      return {
        canvas,
        context: false,
        encodingHex: null,
        lastSeen: performance.now(),
        hatchContours: null,
        hatch: null,
        colorSpace: null,
      };
    });

  return {
    getContext,
    render(encodingHex, parentNode, parentRenderID, opts = {}) {
      const {
        colorSpace = "srgb",
        context,
        width,
        height,
        hatch = true,
        hatchContours = true,
      } = opts;

      let paint = false;
      let node = canvases.find(
        (other) =>
          other.context &&
          match(
            other,
            encodingHex,
            width,
            height,
            hatch,
            hatchContours,
            colorSpace
          )
      );

      const finish = (bitmap) => {
        context.save();
        context.fillStyle = getBackground({
          system: encodingHex.startsWith("01") ? 0x01 : 0x00,
        });
        context.fillRect(0, 0, width, height);
        context.drawImage(bitmap, 0, 0, width, height);
        context.restore();
      };

      if (!node) {
        const oldestIdx = findOldestCanvasIndex(canvases);
        node = canvases[oldestIdx];
        node.hatch = hatch;
        node.hatchContours = hatchContours;
        node.canvas.width = width;
        node.canvas.height = height;
        node.colorSpace = colorSpace;
        node.encodingHex = encodingHex;
        node.renderID = null;
        node.promise = null;
        if (!node.context) {
          node.context = getContext(node.canvas, { colorSpace });
        }

        paint = true;
        node.context.save();
        doRender(encodingHex, node.context, {
          colorSpace,
          width,
          height,
          hatch,
          hatchContours,
        });
        node.context.restore();
      }
      node.lastSeen = performance.now();
      finish(node.canvas);
      return { paint };
    },
  };

  function doRender(encodingHex, context, opts) {
    context.globalAlpha = 1;
    context.clearRect(0, 0, opts.width, opts.height);
    renderToCanvas({
      context,
      ...opts,
      encoding: hexToEncoding(encodingHex),
    });
  }

  function match(
    node,
    encodingHex,
    width,
    height,
    hatch,
    hatchContours,
    colorSpace
  ) {
    if (
      node.canvas.width === width &&
      node.canvas.height === height &&
      node.encodingHex === encodingHex &&
      node.hatch === hatch &&
      node.hatchContours === hatchContours &&
      node.colorSpace === colorSpace
    ) {
      return true;
    }
    return false;
  }

  function getContext(canvas, params) {
    return canvas.getContext("2d", params);
  }
}

export function CanvasPool(opts = {}) {
  const {
    width = 1024,
    height = 1024,
    maxPerFrame = 16,
    maxPaintsPerFrame = 2,
    pixelated = false,
    colorSpace = "srgb",
    hatch = true,
    hatchContours = false,
    maxCanvases = 512,
    initialCanvases = 0,
    sortQueue = true,
    getSortOrder = () => null,
  } = opts;

  const renderQueue = new Set();

  const canvasPool = Array(Math.min(maxCanvases, initialCanvases))
    .fill()
    .map(() => createCanvas());

  const cache = opts.cache || CachedRenderer({ workers: false });

  let rendering = false;
  let raf = null;

  return {
    start() {
      if (!rendering) {
        rendering = true;
        if (raf != null) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(tick);
      }
    },
    stop() {
      if (raf != null) cancelAnimationFrame(raf);
      rendering = false;
    },
    addToRenderQueue(node) {
      if (!renderQueue.has(node)) {
        renderQueue.add(node);
        this.start();
      }
    },
    removeFromRenderQueue(node) {
      if (renderQueue.has(node)) {
        renderQueue.delete(node);
      }
      let poolIndex = node.dataset.poolIndex;
      if (poolIndex != null) {
        poolIndex = parseInt(poolIndex, 10);
        if (isFinite(poolIndex) && poolIndex >= 0) {
          release(poolIndex);
        }
      }
    },
    destroy() {
      renderQueue.clear();
      this.stop();
      for (let i = 0; i < canvasPool.length; i++) {
        release(canvasPool[i]);
      }
      canvasPool.length = 0;
    },
  };

  function release(index) {
    if (canvasPool[index]) {
      const c = canvasPool[index];
      c.active = false;
      if (c.canvas.parentElement) {
        c.canvas.parentElement.removeChild(c.canvas);
      }
      if (c.container) {
        c.container.dataset.poolIndex = "";
        c.container = null;
      }
      if (c.task) {
        if (c.task.abort) c.task.abort();
        c.task = null;
      }
      c.renderID = null;
    }
  }

  function createCanvas() {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.style.width = `100%`;
    canvas.style.height = `auto`;
    if (pixelated) {
      canvas.style.imageRendering = "pixelated";
    }
    return {
      active: false,
      lastSeen: performance.now(),
      lastParams: null,
      container: null,
      canvas,
      context: cache.getContext(canvas, { colorSpace }),
    };
  }

  function paramsMatch(params, lastParams) {
    if (!params || !lastParams) return false;
    return (
      params.width === lastParams.width &&
      params.height === lastParams.height &&
      params.encodingHex === lastParams.encodingHex
    );
  }

  function tick() {
    const elementsToDelete = [];
    const list = [...renderQueue];
    if (sortQueue) {
      const order = getSortOrder() || "asc";
      if (order == "asc") {
        list.sort(
          (a, b) =>
            parseInt(a.dataset.index, 10) - parseInt(b.dataset.index, 10)
        );
      } else {
        list.sort(
          (a, b) =>
            parseInt(b.dataset.index, 10) - parseInt(a.dataset.index, 10)
        );
      }
    }
    for (
      let i = 0, paintCount = 0;
      paintCount < maxPaintsPerFrame && i < maxPerFrame && i < list.length;
      i++
    ) {
      const next = list[i];
      const hints = createRenderTask(next);
      const didPaint = !hints || hints.paint === true || hints.paint == null;
      if (didPaint) {
        paintCount++;
      }
      elementsToDelete.push(next);
    }
    for (let el of elementsToDelete) {
      renderQueue.delete(el);
    }
    if (renderQueue.size > 0) {
      rendering = true;
      raf = requestAnimationFrame(tick);
    } else {
      rendering = false;
    }
  }

  function createRenderTask(el) {
    // get first non active canvas in the pool
    let thumbIdx = canvasPool.findIndex((c) => !c.active);

    // all are active, or there's none in array, so take from oldest or push
    if (thumbIdx == -1) {
      if (canvasPool.length >= maxCanvases) {
        thumbIdx = findOldestCanvasIndex(canvasPool);
        release(thumbIdx); // release old one
      } else {
        canvasPool.push(createCanvas());
        thumbIdx = canvasPool.length - 1;
      }
    }

    // get this thumb
    let thumb = canvasPool[thumbIdx];

    if (thumb.container)
      console.warn("Thumb has container; pool probably has an error");

    // mark as active
    thumb.active = true;
    thumb.container = el;
    thumb.lastSeen = performance.now();
    el.dataset.poolIndex = String(thumbIdx);

    const encodingHex = el.dataset.encoding;
    const newParams = {
      encodingHex,
      width,
      height,
    };

    const currentID = globalID;
    thumb.renderID = currentID;
    // increment global counter
    globalID = (globalID + 1) % BIG_NUMBER;
    // thumb.canvas.style.visibility = "hidden";

    const finish = () => {
      thumb.lastParams = newParams;

      // now add the thumb to the element
      const curChild = el.firstElementChild;
      if (curChild === thumb.canvas) {
        // do nothing, already mounted
      } else {
        // not yet mounted, add it now
        if (curChild) {
          el.removeChild(curChild);
        }
        el.appendChild(thumb.canvas);
      }
    };

    let task = null;
    if (!paramsMatch(newParams, thumb.lastParams)) {
      // async task required
      task = cache.render(encodingHex, thumb, currentID, {
        context: thumb.context,
        colorSpace,
        width,
        height,
        hatch,
        hatchContours,
      });

      thumb.task = task;

      if (task && task.promise) {
        task.promise.then(() => {
          // the render ID of this thumb hasn't changed, we are good to fulfill
          if (thumb.renderID === currentID) {
            finish(); // only apply DOM changes now
            thumb.task = null; // clear task
            thumb.renderID = null; // clear ID
          }
        });
      } else {
        // no promise, it was a sync operation
        finish();
      }
    } else {
      // no async task required, we can finish synchronously
      finish();
    }

    return task;
  }
}
